import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { extractError } from "@/api/client";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { ROLE_LABELS, formatDate } from "@/lib/dict";
import { useAuth } from "@/contexts/AuthContext";
import { Plus, Building2, Users as UsersIcon, Pencil, Trash2, Briefcase, UserCog, Upload, Grid3x3, Database, Mail, ListChecks, Unlock, Lock, ShieldCheck, LifeBuoy } from "lucide-react";
import { toast } from "sonner";
import BulkImportDialog from "@/components/BulkImportDialog";
import AccessMatrix from "@/components/AccessMatrix";
import DataEditor from "@/components/DataEditor";
import PickListsSection from "@/components/PickListsSection";
import HelpContentSection from "@/components/HelpContentSection";

export default function Admin() {
  const { user } = useAuth();
  const isSuper = user?.role === "super_admin";
  const isAdmin = user?.role === "org_admin" || isSuper;

  return (
    <div className="space-y-6">
      <div>
        <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Back office</div>
        <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight text-slate-900 mt-1">Administration</h1>
        <p className="text-sm text-slate-500 mt-1.5">
          {isSuper ? "Vue Super Admin — organisations, utilisateurs, contacts et audit cross-tenant" : "Gestion des utilisateurs, contacts et catalogue de votre organisation"}
        </p>
      </div>

      <Tabs defaultValue="users">
        <TabsList className="bg-white border border-slate-200">
          <TabsTrigger value="users" data-testid="tab-users"><UsersIcon size={13} className="mr-1.5" /> Utilisateurs</TabsTrigger>
          <TabsTrigger value="access" data-testid="tab-access"><Grid3x3 size={13} className="mr-1.5" /> Matrice d'accès</TabsTrigger>
          <TabsTrigger value="contacts" data-testid="tab-contacts"><Briefcase size={13} className="mr-1.5" /> Assureurs / Courtiers / Experts</TabsTrigger>
          {isSuper && <TabsTrigger value="orgs" data-testid="tab-orgs"><Building2 size={13} className="mr-1.5" /> Organisations</TabsTrigger>}
          <TabsTrigger value="data" data-testid="tab-data"><Database size={13} className="mr-1.5" /> Éditeur de données</TabsTrigger>
          <TabsTrigger value="picklists" data-testid="tab-picklists"><ListChecks size={13} className="mr-1.5" /> Listes de choix</TabsTrigger>
          {isAdmin && <TabsTrigger value="security" data-testid="tab-security"><ShieldCheck size={13} className="mr-1.5" /> Sécurité</TabsTrigger>}
          {isAdmin && <TabsTrigger value="helpcenter" data-testid="tab-helpcenter"><LifeBuoy size={13} className="mr-1.5" /> Centre d'aide</TabsTrigger>}
          <TabsTrigger value="audit" data-testid="tab-audit"><UserCog size={13} className="mr-1.5" /> Journal d'audit</TabsTrigger>
        </TabsList>

        <TabsContent value="users" className="mt-4"><UsersSection isAdmin={isAdmin} currentUserId={user?.id} /></TabsContent>
        <TabsContent value="access" className="mt-4"><AccessMatrix /></TabsContent>
        <TabsContent value="contacts" className="mt-4"><ContactsSection isAdmin={isAdmin} /></TabsContent>
        {isSuper && <TabsContent value="orgs" className="mt-4"><OrgsSection /></TabsContent>}
        <TabsContent value="data" className="mt-4"><DataEditor /></TabsContent>
        <TabsContent value="picklists" className="mt-4"><PickListsSection /></TabsContent>
        {isAdmin && <TabsContent value="security" className="mt-4"><SecurityPolicySection /></TabsContent>}
        {isAdmin && <TabsContent value="helpcenter" className="mt-4"><HelpContentSection /></TabsContent>}
        <TabsContent value="audit" className="mt-4"><AuditSection /></TabsContent>
      </Tabs>
    </div>
  );
}

// ----------- USERS -----------
function UsersSection({ isAdmin, currentUserId }) {
  const qc = useQueryClient();
  const { data: users = [] } = useQuery({ queryKey: ["users-all"], queryFn: () => api.get("/users").then((r) => r.data) });
  const { data: units = [] } = useQuery({ queryKey: ["units-all"], queryFn: () => api.get("/units").then((r) => r.data) });
  const { data: assets = [] } = useQuery({ queryKey: ["assets-all"], queryFn: () => api.get("/assets").then((r) => r.data) });
  const [dialog, setDialog] = useState(null); // { mode: 'create' | 'edit', user? }
  const [bulkOpen, setBulkOpen] = useState(false);

  const create = useMutation({
    mutationFn: (d) => api.post("/users", d),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["users-all"] }); toast.success("Utilisateur créé"); setDialog(null); },
    onError: (e) => toast.error(extractError(e, "Échec")),
  });
  const update = useMutation({
    mutationFn: ({ id, patch }) => api.patch(`/users/${id}`, patch),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["users-all"] }); toast.success("Utilisateur mis à jour"); setDialog(null); },
    onError: (e) => toast.error(extractError(e, "Échec")),
  });
  const remove = useMutation({
    mutationFn: (id) => api.delete(`/users/${id}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["users-all"] }); toast.success("Utilisateur supprimé"); },
    onError: (e) => toast.error(extractError(e, "Échec")),
  });
  const sendWelcome = useMutation({
    mutationFn: (id) => api.post(`/users/${id}/send-welcome-email`),
    onSuccess: (_, id) => { const u = users.find((x) => x.id === id); toast.success(`Email d'accès envoyé à ${u?.email || "l'utilisateur"}`); },
    onError: (e) => toast.error(extractError(e, "Échec de l'envoi")),
  });
  const unlock = useMutation({
    mutationFn: (email) => api.post(`/auth/unlock/${encodeURIComponent(email)}`),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ["users-all"] });
      toast.success(res.data.reset_email_sent
        ? "Compte débloqué — un email de réinitialisation du mot de passe a été envoyé"
        : "Compte débloqué (email non envoyé)");
    },
    onError: (e) => toast.error(extractError(e, "Échec du déblocage")),
  });

  return (
    <Card className="border-slate-200 overflow-hidden">
      <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
        <div className="font-display font-semibold">Utilisateurs ({users.length})</div>
        {isAdmin && (
          <div className="flex gap-2">
            <Button data-testid="bulk-import-users" size="sm" variant="outline" onClick={() => setBulkOpen(true)}>
              <Upload size={13} className="mr-1.5" /> Import CSV
            </Button>
            <Button data-testid="new-user-btn" size="sm" className="text-white" style={{ background: "#023E73" }} onClick={() => setDialog({ mode: "create" })}>
              <Plus size={13} className="mr-1.5" /> Nouvel utilisateur
            </Button>
          </div>
        )}
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="bg-slate-50 border-b border-slate-200">
            <th className="text-left px-4 py-2.5 text-[11px] uppercase tracking-wider font-semibold text-slate-500">Nom</th>
            <th className="text-left px-4 py-2.5 text-[11px] uppercase tracking-wider font-semibold text-slate-500">Email</th>
            <th className="text-left px-4 py-2.5 text-[11px] uppercase tracking-wider font-semibold text-slate-500">Rôle</th>
            <th className="text-left px-4 py-2.5 text-[11px] uppercase tracking-wider font-semibold text-slate-500">Société</th>
            <th className="text-left px-4 py-2.5 text-[11px] uppercase tracking-wider font-semibold text-slate-500">Statut</th>
            <th className="text-right px-4 py-2.5 text-[11px] uppercase tracking-wider font-semibold text-slate-500">Actions</th>
          </tr></thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} data-testid={`user-row-${u.id}`} className="border-b border-slate-100 hover:bg-slate-50">
                <td className="px-4 py-2 font-semibold">{u.first_name} {u.last_name}</td>
                <td className="px-4 py-2 text-xs">{u.email}</td>
                <td className="px-4 py-2 text-xs">{ROLE_LABELS[u.role]}</td>
                <td className="px-4 py-2 text-xs">{u.company_name || "—"}</td>
                <td className="px-4 py-2 text-xs">
                  <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-semibold ${u.is_active ? "bg-emerald-100 text-emerald-700" : "bg-slate-200 text-slate-600"}`}>{u.is_active ? "Actif" : "Inactif"}</span>
                  {u.is_locked && (
                    <span data-testid={`locked-badge-${u.id}`} className="ml-1.5 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-red-100 text-red-700">
                      <Lock size={10} /> Verrouillé
                    </span>
                  )}
                </td>
                <td className="px-4 py-2 text-right">
                  {isAdmin && (
                    <div className="inline-flex gap-1">
                      {u.is_locked && (
                        <button
                          data-testid={`unlock-user-${u.id}`}
                          onClick={() => { if (window.confirm(`Débloquer ${u.first_name} ${u.last_name} et lui envoyer un email de réinitialisation du mot de passe ?`)) unlock.mutate(u.email); }}
                          disabled={unlock.isPending}
                          title="Débloquer le compte et envoyer un email de réinitialisation du mot de passe"
                          className="p-1.5 rounded hover:bg-amber-50 text-amber-700 disabled:opacity-30 disabled:cursor-not-allowed"
                        ><Unlock size={13} /></button>
                      )}
                      <button
                        data-testid={`send-welcome-${u.id}`}
                        onClick={() => { if (window.confirm(`Envoyer l'email d'accès à ${u.first_name} ${u.last_name} (${u.email}) ?`)) sendWelcome.mutate(u.id); }}
                        disabled={sendWelcome.isPending || !u.is_active}
                        title={u.is_active ? "Envoyer l'email d'accès (modalités + lien de définition du mot de passe)" : "Utilisateur inactif"}
                        className="p-1.5 rounded hover:bg-blue-50 text-[#023E73] disabled:opacity-30 disabled:cursor-not-allowed"
                      ><Mail size={13} /></button>
                      <button data-testid={`edit-user-${u.id}`} onClick={() => setDialog({ mode: "edit", user: u })} className="p-1.5 rounded hover:bg-slate-100 text-slate-600"><Pencil size={13} /></button>
                      {u.id !== currentUserId && (
                        <button data-testid={`delete-user-${u.id}`} onClick={() => { if (window.confirm(`Supprimer ${u.first_name} ${u.last_name} ?`)) remove.mutate(u.id); }} className="p-1.5 rounded hover:bg-red-50 text-red-600"><Trash2 size={13} /></button>
                      )}
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {dialog && (
        <UserFormDialog
          mode={dialog.mode}
          user={dialog.user}
          units={units}
          assets={assets}
          onClose={() => setDialog(null)}
          onSubmit={(data) => (dialog.mode === "create" ? create.mutate(data) : update.mutate({ id: dialog.user.id, patch: data }))}
        />
      )}
      <BulkImportDialog
        open={bulkOpen}
        onClose={() => setBulkOpen(false)}
        entity="users"
        endpoint="/users/bulk"
        columns={[
          { key: "first_name", label: "Prénom", required: true, example: "Jean" },
          { key: "last_name", label: "Nom", required: true, example: "Dupont" },
          { key: "email", label: "Email", required: true, example: "jean.dupont@demo.fr" },
          { key: "role", label: "Rôle", example: "property_manager" },
          { key: "company_name", label: "Société", example: "" },
          { key: "assigned_asset_ids", label: "Actifs (IDs séparés par |)", example: "asset-001|asset-002" },
          { key: "has_global_access", label: "Accès global (true/false)", example: "false" },
        ]}
        onDone={() => qc.invalidateQueries({ queryKey: ["users-all"] })}
      />
    </Card>
  );
}

function UserFormDialog({ mode, user, units, assets, onClose, onSubmit }) {
  const [form, setForm] = useState({
    first_name: user?.first_name || "", last_name: user?.last_name || "",
    email: user?.email || "", password: "",
    role: user?.role || "read_only",
    company_name: user?.company_name || "", assigned_unit_id: user?.assigned_unit_id || "",
    assigned_asset_ids: user?.assigned_asset_ids || [],
    has_global_access: user?.has_global_access ?? false,
    is_active: user?.is_active ?? true,
  });
  const isTenant = form.role === "tenant";
  const isCompany = form.role === "contractor" || form.role === "insurer" || form.role === "broker";
  const showAssetScope = ["property_manager", "asset_manager", "technical_manager", "accountant", "read_only", "expert"].includes(form.role);
  const toggleAsset = (id) => {
    setForm((f) => ({
      ...f,
      assigned_asset_ids: f.assigned_asset_ids.includes(id)
        ? f.assigned_asset_ids.filter((x) => x !== id)
        : [...f.assigned_asset_ids, id],
    }));
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader><DialogTitle>{mode === "create" ? "Nouvel utilisateur" : "Modifier l'utilisateur"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Prénom</Label><Input data-testid="user-firstname" value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} /></div>
            <div><Label>Nom</Label><Input data-testid="user-lastname" value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} /></div>
          </div>
          <div>
            <Label>Email</Label>
            <Input data-testid="user-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} disabled={mode === "edit"} />
          </div>
          <div>
            <Label>Mot de passe {mode === "edit" && <span className="text-slate-400 text-xs">(laisser vide pour ne pas changer)</span>}</Label>
            <Input data-testid="user-password" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder={mode === "create" ? "Défaut: ChangeMe123!" : "••••••••"} />
          </div>
          <div>
            <Label>Rôle</Label>
            <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
              <SelectTrigger data-testid="user-role"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(ROLE_LABELS).filter(([k]) => k !== "super_admin").map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {isCompany && (
            <div><Label>Société</Label><Input data-testid="user-company" value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} placeholder="Ex: BTP Services SARL" /></div>
          )}
          {isTenant && (
            <div><Label>Lot / unité assignée</Label>
              <Select value={form.assigned_unit_id} onValueChange={(v) => setForm({ ...form, assigned_unit_id: v })}>
                <SelectTrigger data-testid="user-unit"><SelectValue placeholder="Aucun" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Aucun</SelectItem>
                  {units.map((u) => <SelectItem key={u.id} value={u.id}>{u.name} ({u.building}/{u.floor})</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          {showAssetScope && (
            <div>
              <label className="flex items-center gap-2 text-sm cursor-pointer mb-2 p-2 rounded-md border border-slate-200 hover:bg-slate-50">
                <input
                  type="checkbox"
                  data-testid="user-global-access"
                  checked={form.has_global_access}
                  onChange={(e) => setForm({ ...form, has_global_access: e.target.checked })}
                  className="rounded"
                />
                <span className="flex-1">
                  <span className="font-medium">Accès global (tous les actifs)</span>
                  <span className="block text-[11px] text-slate-500">Voit tous les actifs actuels et futurs de l&apos;organisation, sans restriction.</span>
                </span>
              </label>
              <Label>Actifs autorisés {!form.has_global_access && <span className="text-slate-400 text-xs">(aucun site sélectionné = aucun accès)</span>}</Label>
              <div className={`mt-1 border border-slate-200 rounded-lg max-h-40 overflow-y-auto divide-y divide-slate-100 ${form.has_global_access ? "opacity-40 pointer-events-none" : ""}`} data-testid="user-assets">
                {assets.length === 0 && <div className="p-2 text-xs text-slate-400">Aucun actif</div>}
                {assets.map((a) => (
                  <label key={a.id} className="flex items-center gap-2 px-2 py-1.5 cursor-pointer hover:bg-slate-50 text-sm">
                    <input
                      type="checkbox"
                      data-testid={`user-asset-${a.id}`}
                      checked={form.assigned_asset_ids.includes(a.id)}
                      onChange={() => toggleAsset(a.id)}
                      disabled={form.has_global_access}
                      className="rounded"
                    />
                    <span className="flex-1 truncate">{a.name}</span>
                    <span className="text-xs text-slate-500">{a.city}</span>
                  </label>
                ))}
              </div>
              <div className="text-[11px] text-slate-500 mt-1" data-testid="user-access-summary">
                {form.has_global_access
                  ? "Accès global — tous les actifs"
                  : `${form.assigned_asset_ids.length} actif(s) sélectionné(s) · ${form.assigned_asset_ids.length === 0 ? "aucun accès" : "accès limité"}`}
              </div>
            </div>
          )}
          {mode === "edit" && (
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" data-testid="user-active" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} className="rounded" />
              Compte actif
            </label>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annuler</Button>
          <Button
            data-testid="user-submit"
            onClick={() => {
              const payload = { ...form, assigned_unit_id: form.assigned_unit_id === "none" ? null : form.assigned_unit_id };
              // Backend uses strict extra=forbid schemas. Drop fields that don't belong to
              // UserCreateIn (is_active is only on UserUpdateIn) and empty passwords.
              if (mode === "create") delete payload.is_active;
              if (!payload.password) delete payload.password;
              if (mode === "edit") delete payload.email;
              onSubmit(payload);
            }}
            disabled={!form.first_name || !form.last_name || !form.email}
            className="text-white" style={{ background: "#023E73" }}
          >{mode === "create" ? "Créer" : "Enregistrer"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ----------- CONTACTS -----------
const CONTACT_TYPES = { insurer: "Assureur", broker: "Courtier", expert: "Expert" };

function ContactsSection({ isAdmin }) {
  const qc = useQueryClient();
  const { data: contacts = [] } = useQuery({ queryKey: ["contacts"], queryFn: () => api.get("/contacts").then((r) => r.data) });
  const [dialog, setDialog] = useState(null);
  const [bulkOpen, setBulkOpen] = useState(false);

  const create = useMutation({
    mutationFn: (d) => api.post("/contacts", d),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["contacts"] }); toast.success("Contact créé"); setDialog(null); },
  });
  const update = useMutation({
    mutationFn: ({ id, patch }) => api.patch(`/contacts/${id}`, patch),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["contacts"] }); toast.success("Contact mis à jour"); setDialog(null); },
  });
  const remove = useMutation({
    mutationFn: (id) => api.delete(`/contacts/${id}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["contacts"] }); toast.success("Contact supprimé"); },
  });

  const byType = { insurer: [], broker: [], expert: [] };
  contacts.forEach((c) => byType[c.type]?.push(c));

  return (
    <div className="space-y-4">
      <div className="flex justify-end gap-2">
        {isAdmin && (
          <>
            <Button data-testid="bulk-import-contacts" size="sm" variant="outline" onClick={() => setBulkOpen(true)}>
              <Upload size={13} className="mr-1.5" /> Import CSV
            </Button>
            <Button data-testid="new-contact-btn" size="sm" className="text-white" style={{ background: "#023E73" }} onClick={() => setDialog({ mode: "create" })}>
              <Plus size={13} className="mr-1.5" /> Nouveau contact
            </Button>
          </>
        )}
      </div>
      <div className="grid md:grid-cols-3 gap-4">
        {Object.entries(CONTACT_TYPES).map(([type, label]) => (
          <Card key={type} className="p-4 border-slate-200">
            <div className="font-display font-semibold mb-3 flex items-center justify-between">
              <span>{label}s</span><span className="text-xs font-normal text-slate-500">{byType[type].length}</span>
            </div>
            <div className="space-y-2">
              {byType[type].length === 0 && <div className="text-xs text-slate-400">Aucun {label.toLowerCase()}</div>}
              {byType[type].map((c) => (
                <div key={c.id} data-testid={`contact-${c.id}`} className="border border-slate-200 rounded-lg p-2.5 group hover:border-slate-400 transition">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-sm truncate">{c.name}</div>
                      {c.company && <div className="text-xs text-slate-500 truncate">{c.company}</div>}
                      {c.email && <div className="text-[11px] text-slate-500 truncate">{c.email}</div>}
                      {c.phone && <div className="text-[11px] text-slate-500">{c.phone}</div>}
                    </div>
                    {isAdmin && (
                      <div className="flex flex-col gap-1 opacity-0 group-hover:opacity-100 transition">
                        <button data-testid={`edit-contact-${c.id}`} onClick={() => setDialog({ mode: "edit", contact: c })} className="p-1 rounded hover:bg-slate-100 text-slate-600"><Pencil size={11} /></button>
                        <button data-testid={`delete-contact-${c.id}`} onClick={() => { if (window.confirm(`Supprimer ${c.name} ?`)) remove.mutate(c.id); }} className="p-1 rounded hover:bg-red-50 text-red-600"><Trash2 size={11} /></button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </Card>
        ))}
      </div>
      {dialog && (
        <ContactFormDialog
          mode={dialog.mode}
          contact={dialog.contact}
          onClose={() => setDialog(null)}
          onSubmit={(data) => (dialog.mode === "create" ? create.mutate(data) : update.mutate({ id: dialog.contact.id, patch: data }))}
        />
      )}
      <BulkImportDialog
        open={bulkOpen}
        onClose={() => setBulkOpen(false)}
        entity="contacts"
        endpoint="/contacts/bulk"
        columns={[
          { key: "type", label: "Type (insurer/broker/expert)", required: true, example: "insurer" },
          { key: "name", label: "Nom", required: true, example: "AXA Pro" },
          { key: "company", label: "Société", example: "AXA France" },
          { key: "email", label: "Email", example: "contact@axa.fr" },
          { key: "phone", label: "Téléphone", example: "01 40 75 12 34" },
        ]}
        onDone={() => qc.invalidateQueries({ queryKey: ["contacts"] })}
      />
    </div>
  );
}

function ContactFormDialog({ mode, contact, onClose, onSubmit }) {
  const [form, setForm] = useState({
    type: contact?.type || "insurer",
    name: contact?.name || "", company: contact?.company || "",
    email: contact?.email || "", phone: contact?.phone || "", notes: contact?.notes || "",
  });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader><DialogTitle>{mode === "create" ? "Nouveau contact" : "Modifier le contact"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Type</Label>
            <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
              <SelectTrigger data-testid="contact-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(CONTACT_TYPES).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div><Label>Nom</Label><Input data-testid="contact-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Ex: AXA Pro" /></div>
          <div><Label>Société / Cabinet</Label><Input data-testid="contact-company" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Email</Label><Input data-testid="contact-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
            <div><Label>Téléphone</Label><Input data-testid="contact-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
          </div>
          <div><Label>Notes</Label><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annuler</Button>
          <Button data-testid="contact-submit" onClick={() => onSubmit(form)} disabled={!form.name} className="text-white" style={{ background: "#023E73" }}>{mode === "create" ? "Créer" : "Enregistrer"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ----------- ORGANIZATIONS (super_admin) -----------
function OrgsSection() {
  const qc = useQueryClient();
  const { data: orgs = [] } = useQuery({ queryKey: ["organizations"], queryFn: () => api.get("/organizations").then((r) => r.data) });
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", slug: "" });
  const create = useMutation({
    mutationFn: (d) => api.post("/organizations", d),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["organizations"] }); toast.success("Organisation créée"); setOpen(false); setForm({ name: "", slug: "" }); },
    onError: (e) => toast.error(extractError(e, "Échec")),
  });
  return (
    <Card className="border-slate-200 overflow-hidden">
      <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
        <div className="font-display font-semibold">Organisations ({orgs.length})</div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="new-org-btn" size="sm" className="text-white" style={{ background: "#023E73" }}><Plus size={13} className="mr-1.5" /> Nouvelle organisation</Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md">
            <DialogHeader><DialogTitle>Créer une organisation</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Nom</Label><Input data-testid="org-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Foncière Nouvelle" /></div>
              <div><Label>Slug</Label><Input data-testid="org-slug" value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} placeholder="fonciere-nouvelle" /></div>
            </div>
            <DialogFooter>
              <Button data-testid="org-submit" onClick={() => create.mutate(form)} disabled={!form.name} className="text-white" style={{ background: "#023E73" }}>Créer</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
      <table className="w-full text-sm">
        <thead><tr className="bg-slate-50 border-b border-slate-200">
          <th className="text-left px-4 py-2.5 text-[11px] uppercase tracking-wider font-semibold text-slate-500">Nom</th>
          <th className="text-left px-4 py-2.5 text-[11px] uppercase tracking-wider font-semibold text-slate-500">Slug</th>
          <th className="text-right px-4 py-2.5 text-[11px] uppercase tracking-wider font-semibold text-slate-500">Users</th>
          <th className="text-right px-4 py-2.5 text-[11px] uppercase tracking-wider font-semibold text-slate-500">Actifs</th>
          <th className="text-right px-4 py-2.5 text-[11px] uppercase tracking-wider font-semibold text-slate-500">Désordres</th>
          <th className="text-left px-4 py-2.5 text-[11px] uppercase tracking-wider font-semibold text-slate-500">Créée</th>
        </tr></thead>
        <tbody>
          {orgs.map((o) => (
            <tr key={o.id} data-testid={`org-${o.id}`} className="border-b border-slate-100 hover:bg-slate-50">
              <td className="px-4 py-2 font-semibold">{o.name}</td>
              <td className="px-4 py-2 font-mono text-xs">{o.slug}</td>
              <td className="px-4 py-2 text-right font-mono">{o.user_count}</td>
              <td className="px-4 py-2 text-right font-mono">{o.asset_count}</td>
              <td className="px-4 py-2 text-right font-mono">{o.defect_count}</td>
              <td className="px-4 py-2 text-xs">{formatDate(o.created_at)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </Card>
  );
}

// ----------- SECURITY POLICY -----------
function SecurityPolicySection() {
  const qc = useQueryClient();
  const { data: policy } = useQuery({
    queryKey: ["security-policy"],
    queryFn: () => api.get("/organizations/me/security-policy").then((r) => r.data),
  });
  const [form, setForm] = useState(null);
  React.useEffect(() => { if (policy && !form) setForm(policy); }, [policy, form]);

  const save = useMutation({
    mutationFn: (patch) => api.put("/organizations/me/security-policy", patch),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["security-policy"] }); toast.success("Politique de sécurité enregistrée"); },
    onError: (e) => toast.error(extractError(e, "Échec de l'enregistrement")),
  });

  if (!form) return <div className="text-sm text-slate-400 p-4">Chargement…</div>;

  return (
    <Card className="border-slate-200 p-5 max-w-lg">
      <div className="font-display font-semibold flex items-center gap-2 mb-1"><ShieldCheck size={16} className="text-[#023E73]" /> Verrouillage des comptes</div>
      <p className="text-xs text-slate-500 mb-4">Nombre de tentatives de mot de passe échouées avant blocage d'un compte, et durée du blocage. S'applique à tous les utilisateurs de votre organisation.</p>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>Tentatives autorisées</Label>
          <Input
            data-testid="security-max-failures"
            type="number" min={3} max={10}
            value={form.login_max_failures}
            onChange={(e) => setForm({ ...form, login_max_failures: Number(e.target.value) })}
          />
          <div className="text-[11px] text-slate-400 mt-1">Entre 3 et 10</div>
        </div>
        <div>
          <Label>Durée du blocage (minutes)</Label>
          <Input
            data-testid="security-lockout-minutes"
            type="number" min={5} max={120}
            value={form.login_lockout_minutes}
            onChange={(e) => setForm({ ...form, login_lockout_minutes: Number(e.target.value) })}
          />
          <div className="text-[11px] text-slate-400 mt-1">Entre 5 et 120</div>
        </div>
      </div>
      <Button
        data-testid="save-security-policy"
        className="mt-4 text-white"
        style={{ background: "#023E73" }}
        disabled={save.isPending}
        onClick={() => save.mutate({ login_max_failures: form.login_max_failures, login_lockout_minutes: form.login_lockout_minutes })}
      >
        Enregistrer
      </Button>
    </Card>
  );
}

// ----------- AUDIT -----------
function AuditSection() {
  const { data: audits = [] } = useQuery({ queryKey: ["audit-logs"], queryFn: () => api.get("/audit-logs?limit=200").then((r) => r.data) });
  return (
    <Card className="border-slate-200 overflow-hidden">
      <div className="px-4 py-3 border-b border-slate-200 font-display font-semibold">Journal d'audit ({audits.length})</div>
      <div className="divide-y divide-slate-100 max-h-[600px] overflow-y-auto">
        {audits.map((a) => (
          <div key={a.id} className="px-4 py-2 text-xs flex items-center gap-3">
            <div className="text-slate-400 w-40 shrink-0 font-mono">{new Date(a.created_at).toLocaleString("fr-FR")}</div>
            <div className="font-semibold w-32 shrink-0 truncate">{a.user_name || "Système"}</div>
            <div className="text-slate-600 w-24 shrink-0">{a.action}</div>
            <div className="text-slate-400 flex-1">{a.entity_type}</div>
          </div>
        ))}
      </div>
    </Card>
  );
}
